// src/components/legal/TermsPage.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SiteHeader = () => (
  <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex-shrink-0">
          <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel&apos;s Seller Tools</Link>
        </div>
        <nav className="hidden md:flex items-center space-x-2">
          <Link to="/#features" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Features</Link>
          <Link to="/#how-to-use" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">How It Works</Link>
          <Link to="/#pricing" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Pricing</Link>
          <Link to="/how-to-use" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">User Guide</Link>
          <Link to="/blog" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">Blog</Link>
          <Link to="/#faq" className="text-slate-600 hover:text-sky-600 px-3 py-2 rounded-md font-medium">FAQ</Link>
          <a href="/app" className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm">Login</a>
          <Link to="/#pricing" className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-lg">Sign Up Free</Link>
        </nav>
      </div>
    </div>
  </header>
);

const SiteFooter = () => (
  <footer className="bg-slate-800 text-slate-400 py-12 mt-16">
    <div className="max-w-5xl mx-auto text-center px-4">
      <p>&copy; {new Date().getFullYear()} Mr Joel&apos;s Seller Tools. All Rights Reserved.</p>
      <div className="mt-4 space-x-4">
        <Link to="/contact" className="hover:text-white">Contact</Link>
        <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
        <Link to="/terms" className="hover:text-white">Terms of Service</Link>
      </div>
    </div>
  </footer>
);

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Mr Joel&apos;s Seller Tools</title>
        <meta name="description" content="Terms of Service for Mr Joel’s Seller Tools." />
        <meta property="og:title" content="Terms of Service | Mr Joel's Seller Tools" />
        <meta property="og:description" content="Terms of Service for Mr Joel’s Seller Tools." />
        <meta property="og:url" content="https://mrjoelstptgenerator.com/terms" />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 min-h-screen">
        <SiteHeader />

        <main className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-sky-800 tracking-tight">Terms of Service</h1>
            <p className="mt-3 text-slate-600">Effective Date: <strong>June 15, 2025</strong></p>

            <p className="mt-6 text-slate-700">
              These Terms of Service (the “Terms”) govern your access to and use of Mr Joel’s Seller Tools (the “Services”).
              By using the Services, you agree to these Terms. If you are using the Services on behalf of an organization, you
              represent that you have authority to bind that organization to these Terms.
            </p>

            {/* 1) Eligibility */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">1) Eligibility; Account &amp; Security</h2>
              <p className="text-slate-700">
                You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Services.
                You are responsible for maintaining the confidentiality of your account credentials and for all activity
                under your account. Notify us immediately of any unauthorized use.
              </p>
            </section>

            {/* 2) License */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">2) License to Use the Services</h2>
              <p className="text-slate-700">
                Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license
                to access and use the Services for your own business purposes. We reserve all rights not expressly granted.
              </p>
            </section>

            {/* 3) Acceptable Use (corrected) */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">3) Acceptable Use</h2>
              <p className="text-slate-700">You agree not to, and not to attempt to:</p>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                <li>Reverse engineer, decompile, or otherwise attempt to derive the source code of any part of the Services.</li>
                <li>Interfere with or disrupt the integrity or performance of the Services.</li>
                <li>Use the Services for unlawful, harmful, fraudulent, or infringing activity.</li>
                <li>Upload or transmit viruses, malware, or harmful code.</li>
                <li>Scrape or harvest data in a manner that violates any third-party site’s terms or technical restrictions.</li>
                <li>
                  Fail to comply with third-party platform rules (e.g., TPT) or use the Services to bypass
                  their technical or policy restrictions.
                </li>
              </ul>
            </section>

            {/* 4) User Content */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">4) Your Content; AI Features</h2>
              <p className="text-slate-700">
                You retain ownership of content you submit. You grant us a limited license to process your content
                to operate, improve, and provide the Services. AI outputs may be inaccurate or incomplete; you are
                responsible for reviewing and editing outputs before use.
              </p>
              <p className="text-slate-700">
                Some features send your prompts and context to third-party AI providers (e.g., Google Generative AI)
                under their terms. We do not use your content to train our own models.
              </p>
            </section>

            {/* 5) Billing */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">5) Billing; Access Activation</h2>
              <p className="text-slate-700">
                Purchases are processed on Teachers Pay Teachers (“TPT”). We do not collect or store payment card
                details. Any fees are non-refundable unless required by law.
              </p>
              <p className="text-slate-700">
                If you purchase access described as “lifetime,” the scope and duration of that access are governed
                by Section 16 (Lifetime Access; Service Availability).
              </p>
            </section>

            {/* 6) Third Party & TPT Risk */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">6) Third-Party Services</h2>
              <p className="text-slate-700">
                The Services may depend on or interoperate with third-party services and websites. We do not control
                and are not responsible for third-party services, their availability, performance, or terms.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-3">TPT Compliance &amp; Responsibility</h3>
              <p className="text-slate-700">
                We are not affiliated with Teachers Pay Teachers (“TPT”). Your use of TPT is governed solely by
                TPT’s terms and policies. You are solely responsible for ensuring that your use of the Services
                complies with TPT’s rules. We are not responsible for any actions TPT takes on your store or account.
              </p>

              <p className="text-slate-700 mt-3">
                <strong>Platform Changes &amp; Breakage Risk.</strong><br />
                Third-party platforms (including TPT) may modify, restrict, or remove access to pages, data, or
                interfaces at any time. Such changes may impact or disable features of the Services. We do not
                guarantee ongoing compatibility with TPT or any third-party platform. You assume this risk when
                using the Services.
              </p>
            </section>

            {/* 7) IP */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">7) Intellectual Property</h2>
              <p className="text-slate-700">
                The Services (including software, code, design, and text) are owned by us or our licensors and are
                protected by intellectual property laws.
              </p>
            </section>

            {/* 8) Privacy */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">8) Privacy</h2>
              <p className="text-slate-700">
                Our <Link to="/privacy-policy" className="text-sky-700 underline">Privacy Policy</Link> explains how
                we collect, use, and share information. By using the Services, you agree to our Privacy Policy.
              </p>
            </section>

            {/* 9) Warranty */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">9) Disclaimer of Warranties</h2>
              <p className="text-slate-700">
                THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
                IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                PURPOSE, AND NON-INFRINGEMENT. AI OUTPUTS AND THIRD-PARTY DATA MAY BE INACCURATE OR INCOMPLETE.
              </p>
            </section>

            {/* 10) Liability */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">10) Limitation of Liability</h2>
              <p className="text-slate-700">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE
                SERVICES IS LIMITED TO THE AMOUNT YOU PAID (IF ANY) IN THE 12 MONTHS PRIOR TO THE EVENT.
              </p>
            </section>

            {/* 11) Indemnification */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">11) Indemnification</h2>
              <p className="text-slate-700">
                You will indemnify and hold us harmless from any claims or losses arising from your content, your use
                of the Services, or your violation of these Terms or applicable law.
              </p>
            </section>

            {/* 12) Termination */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">12) Suspension; Termination</h2>
              <p className="text-slate-700">
                We may suspend or terminate your access if you violate these Terms.
              </p>
              <p className="text-slate-700">
                We may also discontinue or sunset the Services (in whole or in part) for business, technical,
                legal, or operational reasons. Where reasonably practicable, we will provide advance notice.
              </p>
              <p className="text-slate-700">
                Sections that should survive termination will survive (including ownership, disclaimers,
                limitations, and lifetime access clauses).
              </p>
            </section>

            {/* 13) Changes */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">13) Changes to the Services or Terms</h2>
              <p className="text-slate-700">
                We may update or remove features at any time. We may modify these Terms, and the Effective Date
                will be updated when changes occur. Continued use after changes constitutes acceptance.
              </p>
            </section>

            {/* 14) Governing Law */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">14) Governing Law; Dispute Resolution</h2>
              <p className="text-slate-700">
                These Terms are governed by the laws of your state or country of residence. Disputes will be
                resolved in the courts located in our principal place of business unless otherwise required by law.
              </p>
            </section>

            {/* 15) Contact */}
            <section className="mt-10 space-y-4">
              <h2 className="text-2xl font-bold text-slate-800">15) Contact</h2>
              <p className="text-slate-700">
                Questions? <a href="mailto:info@mrjoelstptgenerator.com" className="text-sky-700 underline">
                  info@mrjoelstptgenerator.com
                </a>
              </p>
            </section>

            {/* 16) Lifetime Access */}
            <section className="mt-16 space-y-4 border-t border-slate-300 pt-10">
              <h2 className="text-2xl font-bold text-slate-800">16) Lifetime Access; Service Availability</h2>

              <p className="text-slate-700">
                Some plans or licenses are marketed as providing “lifetime” access. For clarity:
              </p>

              <ul className="list-disc list-inside text-slate-700 space-y-3">

                <li>
                  <strong>Meaning of “Lifetime”.</strong><br />
                  “Lifetime” refers to the lifetime of the Services (or the applicable plan), not your lifetime or the
                  lifetime of any third-party platform.
                </li>

                <li>
                  <strong>No Guarantee of Permanence.</strong><br />
                  Features may be modified, limited, or removed if third parties restrict access, if required by law, or
                  if they become commercially or technically unfeasible.
                </li>

                <li>
                  <strong>Right to Discontinue.</strong><br />
                  We may discontinue or sunset the Services for legitimate business, technical, legal, or operational
                  reasons. If this occurs, lifetime access ends when the Services end.
                </li>

                <li>
                  <strong>TPT &amp; Third-Party Dependency.</strong><br />
                  If TPT or any third-party platform changes, blocks, or limits access to data or functionality, affected
                  features may stop working. This risk is inherent when using tools that interact with third-party platforms.
                </li>

                <li>
                  <strong>No Refund Obligation.</strong><br />
                  Except where required by law, we are not obligated to issue refunds, credits, or replacements if the
                  Services or lifetime features are modified, limited, or discontinued.
                </li>

              </ul>
            </section>

          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
};

export default TermsPage;
